import { SupabaseClient } from "@supabase/supabase-js";

import {
  DEFAULT_INCLUDE_GLOBAL_CHAIN_REP,
  EnterpriseRepPreferences,
  NO_ENTERPRISE_REPS,
} from "@/lib/db/hotel-chain-rep-precedence";

import { PreferredChainRepRowDb, PreferredVenueRepRowDb } from "./types/preferred-chain-types";

/** The enterprise-wide opt-in, embedded on each preferred row so it costs no extra query. */
const ENTERPRISE_SETTING_EMBED = "enterprise_organizations:organization_id(include_global_chain_rep)";

/** Normalizes rep_emails from JSONB — handles both array and double-serialized string. */
function parseRepEmails(raw: unknown): { email: string }[] {
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
    } catch {
      return [];
    }
  }
  return [];
}

/**
 * Looks up an enterprise's preferred reps for a venue by checking both
 * enterprise_preferred_venues (direct match) and enterprise_preferred_chains (chain match),
 * along with whether the enterprise keeps our global chain rep copied for this chain.
 *
 * The opt-in is read from the chain row's override (a brand's own row before the chain-wide
 * one), else the enterprise-wide setting embedded on whichever preferred row matched. When no
 * row matches there are no enterprise reps, and the opt-in has nothing to decide.
 *
 * @param supabase - Supabase client. MUST be service-role: enterprise_preferred_venues
 *   and enterprise_preferred_chains grant SELECT to service_role only, so an
 *   authenticated client gets permission-denied and this returns no reps.
 * @param enterpriseOrgId - The enterprise_organizations.id (callers must resolve from org_id first).
 * @param venueStaticId - The venue static ID to match against preferred venues.
 * @param chainId - Optional chain ID to match against preferred chains.
 * @param brandId - Optional brand ID for brand-specific chain matching.
 * @returns Deduplicated, lowercased rep emails and the resolved global-chain-rep opt-in.
 * @throws Never — read failures are logged and resolve to no reps.
 */
export async function getEnterpriseRepPreferences(
  supabase: SupabaseClient,
  enterpriseOrgId: number,
  venueStaticId: number,
  chainId?: number | null,
  brandId?: number | null
): Promise<EnterpriseRepPreferences> {
  if (!enterpriseOrgId || !venueStaticId) return NO_ENTERPRISE_REPS;

  const venueQuery = supabase
    .schema("nowadays")
    .from("enterprise_preferred_venues")
    .select(`rep_emails, ${ENTERPRISE_SETTING_EMBED}`)
    .eq("organization_id", enterpriseOrgId)
    .eq("venue_static_id", venueStaticId)
    .maybeSingle();

  const chainQuery = chainId
    ? supabase
        .schema("nowadays")
        .from("enterprise_preferred_chains")
        .select(`rep_emails, brand_id, include_global_chain_rep, ${ENTERPRISE_SETTING_EMBED}`)
        .eq("organization_id", enterpriseOrgId)
        .eq("chain_id", chainId)
    : null;

  const [{ data: venueData, error: venueError }, chainResult] = await Promise.all([
    venueQuery,
    chainQuery ?? Promise.resolve({ data: null, error: null }),
  ]);

  // Surface read failures instead of swallowing them. These tables grant SELECT
  // to service_role only, so an authenticated client returns a permission error
  // (Postgres 42501) that is otherwise indistinguishable from "no reps
  // configured" — logging it turns a silent CC-nobody data loss into a signal
  // the wrong client was passed (the bug ENG-352 fixed).
  if (venueError || chainResult.error) {
    const describeError = (e: { code?: string; message?: string } | null) =>
      e ? { code: e.code, message: e.message } : null;
    console.error("[enterprise-reps] failed to read preferred rep emails (needs service-role client):", {
      enterpriseOrgId,
      venueError: describeError(venueError),
      chainError: describeError(chainResult.error),
    });
  }

  const venueRow = venueData as PreferredVenueRepRowDb | null;
  const chainRows = ((chainResult.data as PreferredChainRepRowDb[] | null) ?? []).filter(
    (row) => row.brand_id == null || row.brand_id === brandId
  );

  const emails = new Set<string>();
  for (const rep of parseRepEmails(venueRow?.rep_emails)) {
    if (rep.email) emails.add(rep.email.toLowerCase());
  }
  for (const row of chainRows) {
    for (const rep of parseRepEmails(row.rep_emails)) {
      if (rep.email) emails.add(rep.email.toLowerCase());
    }
  }

  // A brand's own row is more specific than the chain-wide row, so its choice is read first.
  const chainOverride =
    chainRows.find((row) => row.brand_id != null)?.include_global_chain_rep ??
    chainRows.find((row) => row.brand_id == null)?.include_global_chain_rep;
  const enterpriseSetting =
    (chainRows[0] ?? venueRow)?.enterprise_organizations?.include_global_chain_rep ?? DEFAULT_INCLUDE_GLOBAL_CHAIN_REP;

  return { emails: Array.from(emails), includeGlobalChainRep: chainOverride ?? enterpriseSetting };
}
